import React, {
  useState,
  useEffect,
  useRef,
  Dispatch,
  ChangeEvent,
  useCallback,
} from "react";
import Draggable from "react-draggable";
import "../../App.css";
import { ResizableBox, ResizeCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";
import loadIco from "../../assets/loading-ico.gif";
import closeIco from "../../assets/close-icon-color.png";
import { NotificationType } from "./NotificationPopup";
import DataTable, { GenerateEarlyWarningTypes } from "./DataTable";
import ViewMap from "../../simulation/SimulationPopUp/SimulationPopUpMap/ViewSimulationMap";
import ViewForecastMap from "../../simulation/Forecast/ForecastMap/ViewForecastMap";
import { ItemTypes } from "../../initial/menu/RightSideMenu";

type SimulationPopupProps = {
  terminalState: Dispatch<React.SetStateAction<boolean>>;
  terminalCommands: Dispatch<React.SetStateAction<string[]>>;
  notifications: Dispatch<React.SetStateAction<NotificationType[]>>;
  handleClosePopup: () => void;
  runButtonOnClick: (simulationName: string) => void;
  addItem?: (id: string, type: ItemTypes) => void;
  id?: string;
};

const SimulationPopup = (props: SimulationPopupProps) => {
  const [showDialog, setShowDialog] = useState<boolean>(true);
  const [showSensorDetails, setShowSensorDetails] = useState(false);
  const [activeFullScreen, setActiveFullScreen] = useState<boolean>(false);
  const fullscreenModal = useRef<HTMLDivElement | null>(null);
  const parentContainerRef = useRef<HTMLDivElement | null>(null);
  const [showDescription, setShowDescription] = useState(false);
  const [widthMain, setWidthMain] = useState<number>(0);
  const [heightMain, setHeightMain] = useState<number>(0);

  const [dimensions, setDimensions] = useState({
    resizableBoxwidth: 0,
    resizableBoxheight: 0,
  });

  const [dimensionsMin, setDimensionsMin] = useState({
    resizableBoxMinwidth: 0,
    resizableBoxMinheight: 0,
  });

  //----------------resibleBox Function----------------

  const updateDimensions = () => {
    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
    const vh = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    );

    if (vw > 2000 && vh > 1200) {
      setDimensions({
        resizableBoxwidth: vw * 0.4,
        resizableBoxheight: vh * 0.5,
      });
    } else {
      setDimensions({
        resizableBoxwidth: vw * 0.6,
        resizableBoxheight: vh * 0.65,
      });
    }

    if (vw > 2000 && vh > 1200) {
      setDimensionsMin({
        resizableBoxMinwidth: vw * 0.2,
        resizableBoxMinheight: vh * 0.3,
      });
    } else {
      setDimensionsMin({
        resizableBoxMinwidth: vw * 0.3,
        resizableBoxMinheight: vh * 0.4,
      });
    }
  };

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const openContentFullscreen = () => {
    if (!document.fullscreenElement) {
      enterFullscreen();
      setShowSensorDetails(true);
    } else {
      exitFullScreen();
      setShowSensorDetails(false);
    }
  };

  const enterFullscreen = () => {
    const elem = fullscreenModal.current;
    if (elem) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      }
      setActiveFullScreen(true);
    }
  };

  const exitFullScreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      (document as any).mozCancelFullScreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
    setActiveFullScreen(false);
  };

  const hideDialog = () => {
    setShowDialog(false);
    props.handleClosePopup();
  };

  useEffect(() => {}, [showDescription]);

  useEffect(() => {}, [widthMain, heightMain]);

  const handleResize = (
    event: React.SyntheticEvent,
    data: ResizeCallbackData
  ) => {
    setWidthMain(data.size.width);
    setHeightMain(data.size.height);

    if (data.size.width < 900) {
      // Set your desired width threshold
      setShowDescription(true);
    } else {
      setShowDescription(false);
    }
  };

  /* New start */
  // Selected tab
  const [selectedTab, setSelectedTab] = useState<
    | "simulation_tab"
    | "forecast_tab"
    | "analyse_impact_tab"
    | "generate_early_warning_tab"
  >("simulation_tab");

  // custom value status
  const [isCustomValueOpen, setIsCustomValueOpen] = useState<boolean>(false);
  // Loading screen
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Data
  // Flood model type data
  const [floodModelTypes, setFloodModelTypes] = useState<
    { name: string; value: string }[]
  >([
    { name: "GloFas", value: "GloFas" },
    { name: "Anuga", value: "Anuga" },
    { name: "Hecras", value: "Hecras" },
    { name: "CMOD", value: "CMOD" },
  ]);
  // Forecast type data
  const [forecastTypes, setForecastTypes] = useState<
    { name: string; value: string }[]
  >([
    {name: 'ESWF', value: 'ESWF'},
		{name: 'GWF', value: 'GWF'},
  ]);
  // River sensors data
  const [riverSensorTypes, setRiverSensorTypes] = useState<
    { name: string; value: string }[]
  >([
    {name: 'Irrigation Department', value: 'Irrigation Department'},
		{name: 'NBRO Department', value: 'NBRO Department'},
		{name: 'Community', value: 'Community'},
  ]);
  // River sensors data
  const [buildingDataFrom, setBuildingDataFrom] = useState<
    { name: string; value: string }[]
  >([
    { name: "Irrigation Department", value: "Irrigation Department" },
    { name: "NBRO Department", value: "NBRO Department" },
    { name: "Met Office", value: "Met Office" },
    { name: "Community", value: "Community" },
  ]);

  // Input values
  const simulationNameRef = useRef<HTMLInputElement | null>(null);
  const simulationFloodModelRef = useRef<HTMLSelectElement | null>(null);
  const simulationForecastRef = useRef<HTMLSelectElement | null>(null);
  const simulationRiverSensorsRef = useRef<HTMLSelectElement | null>(null);
  const forecastShowCatchmentAreaRef = useRef<HTMLInputElement | null>(null);
  const forecastPublishRef = useRef<HTMLInputElement | null>(null);

  // Handle simulation tab next button click
  const handleSimulationTabNextButtonClick = () => {
    setIsLoading(true);
    props.terminalState(true);
    props.terminalCommands((prevState) => [
      ...prevState,
      "terminal starting....",
    ]);
    let count = 0;
    const terminalData = setInterval(() => {
      props.terminalCommands((prevState) => [...prevState, "command " + count]);
      count += 1;

      if (count > 10) {
        clearTimeout(terminalData);
        props.terminalCommands((prevState) => [
          ...prevState,
          "terminal ended....",
        ]);
        setIsLoading(false);
        props.notifications((prevState) => [
          ...prevState,
          { type: "success", message: "test successfully completed" },
        ]);
      }
    }, 500);
  };

  // Handle forecast tab next button click
  const handleForecastTabNextButtonClick = () => {
    console.log("forecast tab next button clicked");
    setSelectedTab("analyse_impact_tab");
  };

  // Handle analyse impact tab next button click
  const handleAnalyseImpactTabNextButtonClick = () => {
    console.log("analyse impact tab next button clicked");
    setSelectedTab("generate_early_warning_tab");
  };

  // Handle generate ew tab next button click
  const handleGenerateEWTabNextButtonClick = () => {
    console.log("generate ew tab trigger button clicked");
  };

  const rows: GenerateEarlyWarningTypes[] = [
    {
      id: 51040,
      buildingId: 51040,
      buildingType: "BLDGA",
      buildingOwnerName: "Nihal Perera",
      mobileNumber: "0711111111",
      areaOfRisk: "91780.2011292",
      maximumDepth: 200,
      startTime: "05/01/2024, 18:00",
      peakTime: "05/01/2024, 18:00",
    },
    {
      id: 51041,
      buildingId: 51041,
      buildingType: "BLDGA",
      buildingOwnerName: "Gihan Fernando",
      mobileNumber: "0711111111",
      areaOfRisk: "91780.2011292",
      maximumDepth: 200,
      startTime: "05/01/2024, 18:00",
      peakTime: "05/01/2024, 18:00",
    },
  ];


  const handleInputChange = () => {
    if (simulationNameRef.current) {
        props.runButtonOnClick(simulationNameRef.current.value);
    }
  };

  const popupRef = useRef<HTMLDivElement | null>(null)
  const enteredRef = useRef<boolean>(false)

  const mouseMoveEvent = useCallback((event: MouseEvent) => {
    const sideMenu = document.getElementById('side-menu-wrapper');
    
    if (sideMenu && !enteredRef.current) {
        const sideMenuWidth = Number(window.getComputedStyle(sideMenu).left.slice(0, -2));

        if (sideMenuWidth < event.clientX) {
          enteredRef.current = true
          if(props.id && props.addItem){
            props.addItem(props.id, 'simulation')
            /* props.windowState(false) */
            setShowDialog(false)
          }
        }
    }
}, [])

const touchMoveEvent = useCallback((event: TouchEvent) => {
  const sideMenu = document.getElementById('side-menu-wrapper');
  
  if (sideMenu && !enteredRef.current) {
      const sideMenuWidth = Number(window.getComputedStyle(sideMenu).left.slice(0, -2));

      const touchX = event.touches[0].clientX; 

      if (sideMenuWidth < touchX) {
        enteredRef.current = true
        if(props.id && props.addItem){
          props.addItem(props.id, 'simulation')
          /* props.windowState(false) */
          setShowDialog(false)
        }
      }
  }
}, [])

 useEffect(() => {
    const handleMouseDown = () => {
      document.addEventListener("mousemove", mouseMoveEvent);
      document.addEventListener("mouseup", handleMouseUp);
    };

    const handleTouchStart = () => {
      document.addEventListener("touchmove", touchMoveEvent);
      document.addEventListener("touchend", handleTouchEnd);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", mouseMoveEvent);
    };

    const handleTouchEnd = () => {
      document.removeEventListener("touchmove", touchMoveEvent);
    };

    if (popupRef.current) {
      popupRef.current.addEventListener("mouseenter", () => {
        popupRef.current?.addEventListener("mousedown", handleMouseDown);
        popupRef.current?.addEventListener("touchstart", handleTouchStart);
      });
    }

    return () => {
      document.removeEventListener("mousemove", mouseMoveEvent);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", touchMoveEvent);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [mouseMoveEvent, touchMoveEvent]);

  return (
    <div className="ol-overlay-container ol-selectable">
      <div
        style={{
          visibility: showDialog ? "visible" : "hidden",
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
        ref={parentContainerRef}
      >
        <Draggable
          handle=".titleContainer"
          cancel=".no-drag"
          enableUserSelectHack={false}
        >
          <ResizableBox
            className="custom-resizable-box"
            width={dimensions.resizableBoxwidth}
            height={dimensions.resizableBoxheight}
            minConstraints={[
              dimensionsMin.resizableBoxMinwidth,
              dimensionsMin.resizableBoxMinheight,
            ]}
            onResize={handleResize}
            resizeHandles={["se", "sw", "ne", "nw"]}
          >
            <div
              className="drag-handle"
              ref={fullscreenModal}
              style={{
                bottom: 0,
                margin: "auto",
                width: "100%",
                height: "100%",
                position: "absolute",
                cursor: "move",
                touchAction: "none",
              }}
            >
              <div className={`simulationPopupContainer`}>
                {/* Title container */}
                <div className={`titleContainer`}  ref={popupRef}>
                  <span>simulation</span>
                  <div className={`titleButtonContainer`}>
                    <i
                      className={`titleIcon fullScreenIco`}
                      onClick={openContentFullscreen}
                      onTouchStart={openContentFullscreen}
                    ></i>
                    <i
                      className={`titleIcon closeIco`}
                      //   onClick={(e) => props.windowState(prevState => false)}
                      onClick={hideDialog}
                      onTouchStart={hideDialog}
                    ></i>
                  </div>
                </div>
                {/* Tab area */}
                <div className={`tabArea`}>
                  <div
                    className={`tabButton ${
                      selectedTab === "simulation_tab" ? "tabSelected" : ""
                    }`}
                    id="simulation_tab"
                    onClick={(e) =>
                      setSelectedTab((prevState) => "simulation_tab")
                    }
                    onTouchStart={(e) =>
                      setSelectedTab((prevState) => "simulation_tab")}
                  >
                    simulation
                  </div>
                  <div
                    className={`tabButton ${
                      selectedTab === "forecast_tab" ? "tabSelected" : ""
                    }`}
                    id="forecast_tab"
                    onClick={(e) =>
                      setSelectedTab((prevState) => "forecast_tab")
                    }
                    onTouchStart={(e) =>
                      setSelectedTab((prevState) => "forecast_tab")}
                  >
                    forecast
                  </div>
                  <div
                    className={`tabButton ${
                      selectedTab === "analyse_impact_tab" ? "tabSelected" : ""
                    }`}
                    id="analyse_impact_tab"
                    onClick={(e) =>
                      setSelectedTab((prevState) => "analyse_impact_tab")
                    }
                    onTouchStart={(e) =>
                      setSelectedTab((prevState) => "analyse_impact_tab")}
                  >
                    analyse impact
                  </div>
                  <div
                    className={`tabButton ${
                      selectedTab === "generate_early_warning_tab"
                        ? "tabSelected"
                        : ""
                    }`}
                    id="generate_early_warning_tab"
                    onClick={(e) =>
                      setSelectedTab(
                        (prevState) => "generate_early_warning_tab"
                      )
                    }
                    onTouchStart={(e) =>
                      setSelectedTab(
                        (prevState) => "generate_early_warning_tab"
                      )}
                  >
                    generate early warning
                  </div>
                </div>

                {/* Simulation data container */}
                {selectedTab === "simulation_tab" && (
                  <div className={`simulationTabDataWrapper tabDataContainer`}>
                    {/* Left data container */}
                    <div className={`leftDataContainer`}>
                      <div className={`inputItemContainer`}>
                        <label htmlFor="simulation_popup_simulation_name_input">
                          simulation name
                        </label>
                        <input
                          id="simulation_popup_simulation_name_input"
                          type="text"
                          placeholder="Enter Simulation name"
                          ref={simulationNameRef}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className={`inputItemContainer`}>
                        <label htmlFor="simulation_popup_flood_model_select">
                          flood model type
                        </label>
                        <select
                          name="flood_model_type"
                          id="simulation_popup_flood_model_select"
                          ref={simulationFloodModelRef}
                        >
                          <option value={""} disabled selected>
                            Select...
                          </option>
                          {floodModelTypes.map((option, index) => (
                            <option key={index} value={option.value}>
                              {option.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className={`inputItemContainer`}>
                        <label htmlFor="simulation_popup_forecast_select">
                          forecast
                        </label>
                        <select
                          name="forecast_type"
                          id="simulation_popup_forecast_select"
                          ref={simulationForecastRef}
                        >
                          <option value={""} disabled selected>
                            Select...
                          </option>
                          {forecastTypes.map((option, index) => (
                            <option key={index} value={option.value}>
                              {option.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className={`customValueWrapper`}>
                        <div
                          className={`titleWrapper`}
                          onClick={(e) =>
                            setIsCustomValueOpen((prevStatus) => !prevStatus)
                          }
                          onTouchStart={(e) =>
                            setIsCustomValueOpen((prevStatus) => !prevStatus)
                          }
                        >
                          <span className={`title`}>custom value</span>
                          <div
                            className={`icon ${
                              isCustomValueOpen ? "bgUp" : "bgDown"
                            }`}
                          ></div>
                        </div>
                        <div
                          className={`dataContainer`}
                          style={{
                            display: isCustomValueOpen ? "block" : "none",
                          }}
                        >
                          <div className={`inputItemContainer`}>
                            <label htmlFor="simulation_popup_river_sensors_select">
                              river sensors
                            </label>
                            <select
                              name="river_sensor_type"
                              id="simulation_popup_river_sensors_select"
                              ref={simulationRiverSensorsRef}
                            >
                              <option value={""} disabled selected>
                                Select...
                              </option>
                              {riverSensorTypes.map((option, index) => (
                                <option key={index} value={option.value}>
                                  {option.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Right map container */}
                    <div className={`rightMapContainer`}>
                      {/* Map here */}

                      <ViewMap />
                    </div>
                    <div className={`bottomContainer`}>
                      <button
                        onClick={(e) => handleSimulationTabNextButtonClick()}
                        onTouchStart={(e) => handleSimulationTabNextButtonClick()}
                      >
                        run
                      </button>
                    </div>
                  </div>
                )}

                {/* Forecast data container */}
                {selectedTab === "forecast_tab" && (
                  <div className={`forecastTabDataWrapper tabDataContainer`}>
                    <div className={`forecastMap`}>
                      {/* Forecast map here */}
                      <ViewForecastMap />
                    </div>
                    <div className={`bottomMenu`}>
                      <input
                        type="checkbox"
                        name=""
                        id="simulation_popup_forecast_show_catchment_area"
                        ref={forecastShowCatchmentAreaRef}
                      />
                      <label htmlFor="simulation_popup_forecast_show_catchment_area">
                        show catchment area
                      </label>

                      <input
                        type="checkbox"
                        name=""
                        id="simulation_popup_publish"
                        ref={forecastPublishRef}
                      />
                      <label htmlFor="simulation_popup_publish">publish</label>

                      <button
                        onClick={(e) => handleForecastTabNextButtonClick()}
                        onTouchStart={(e) => handleForecastTabNextButtonClick()}
                      >
                        next
                      </button>
                    </div>
                  </div>
                )}

                {/* Analyse impact data container */}
                {selectedTab === "analyse_impact_tab" && (
                  <div
                    className={`analyseImpactTabDataWrapper tabDataContainer`}
                  >
                    <span className={`titleContainer`}>
                      Select to generate early warning
                    </span>
                    <div className={`buildingDataFromSelector`}>
                      <label htmlFor="">Building Data From</label>
                      <select name="" id="">
                        <option value={""} disabled selected>
                          Select...
                        </option>
                        {buildingDataFrom.map((option, index) => (
                          <option key={index} value={option.value}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={`checkBoxContainer`}>
                      <div className={`item`}>
                        <input
                          type="checkbox"
                          name=""
                          id="simulation_popup_buildingID"
                          ref={forecastPublishRef}
                        />
                        <label htmlFor="simulation_popup_buildingID">
                          Building ID
                        </label>
                      </div>
                      <div className={`item`}>
                        <input
                          type="checkbox"
                          name=""
                          id="simulation_popup_building_owner_name"
                          ref={forecastPublishRef}
                        />
                        <label htmlFor="simulation_popup_building_owner_name">
                          Building Owner Name
                        </label>
                      </div>
                      <div className={`item`}>
                        <input
                          type="checkbox"
                          name=""
                          id="simulation_popup_mobile_number"
                          ref={forecastPublishRef}
                        />
                        <label htmlFor="simulation_popup_mobile_number">
                          Mobile Number
                        </label>
                      </div>
                      <div className={`item`}>
                        <input
                          type="checkbox"
                          name=""
                          id="simulation_popup_building_type"
                          ref={forecastPublishRef}
                        />
                        <label htmlFor="simulation_popup_building_type">
                          Building Type
                        </label>
                      </div>
                      <div className={`item`}>
                        <input
                          type="checkbox"
                          name=""
                          id="simulation_popup_maximum_depth"
                          ref={forecastPublishRef}
                        />
                        <label htmlFor="simulation_popup_maximum_depth">
                          Maximum Depth
                        </label>
                      </div>
                      <div className={`item`}>
                        <input
                          type="checkbox"
                          name=""
                          id="simulation_popup_start_time"
                          ref={forecastPublishRef}
                        />
                        <label htmlFor="simulation_popup_start_time">
                          Start Time
                        </label>
                      </div>
                      <div className={`item`}>
                        <input
                          type="checkbox"
                          name=""
                          id="simulation_popup_peak_type"
                          ref={forecastPublishRef}
                        />
                        <label htmlFor="simulation_popup_peak_type">
                          Peak Type
                        </label>
                      </div>
                    </div>
                    <div className={`bottomContainer`}>
                      <button
                        onClick={(e) => handleAnalyseImpactTabNextButtonClick()}
                        onTouchStart={(e) => handleAnalyseImpactTabNextButtonClick()}
                      >
                        next
                      </button>
                    </div>
                  </div>
                )}

                {/* Generate early warning data container */}
                {selectedTab === "generate_early_warning_tab" && (
                  <div
                    className={`generateEarlyWarningTabDataWrapper tabDataContainer`}
                  >
                    <div className={`tableContainer`}>
                      {/* Table here */}
                      <DataTable data={rows} />
                    </div>

                    <div className={`bottomContainer`}>
                      <button
                        onClick={(e) => handleGenerateEWTabNextButtonClick()}
                        onTouchStart={(e) => handleGenerateEWTabNextButtonClick()}
                      >
                        trigger
                      </button>
                    </div>
                  </div>
                )}

                {/* Loading screen */}
                {isLoading && (
                  <div className={`loadingScreen`}>
                    <img className={`loadImg`} src={loadIco} alt="" />
                    <img
                      className={`closeImg`}
                      src={closeIco}
                      alt=""
                      onClick={(e) => {
                        setIsLoading(false);
                        props.terminalState(false);
                        props.terminalCommands([]);
                      }}
                      onTouchStart={(e) => {
                        setIsLoading(false);
                        props.terminalState(false);
                        props.terminalCommands([]);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </ResizableBox>
        </Draggable>
      </div>
    </div>
  );
};
export default SimulationPopup;
